import React, { useState, useEffect } from "react";
import { Typography, CircularProgress } from "@material-ui/core";
import { Formik } from "formik";
import * as yup from "yup";
import toBase64 from "../../utils/toBase64";
import domtoimage from "dom-to-image";
import jpegToBlob from "../../utils/jpegToBlob";
import {
  Dialog,
  DialogContent,
  Box,
  DialogActions,
  Button,
  FormControl,
  DialogTitle,
} from "@material-ui/core";
import api from "../lib/api";

import {
  StyledTextField,
  StyledCircleButton,
  StyledCheckCircleIcon,
  StyledFormLabel,
} from "./common.styles";

export const submitValidationSchema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required(),
  company: yup.string(),
  phone: yup.number().typeError("phone must be a number"),
  comment: yup.string(),
});

const SubmitModal = ({
  modalOpen,
  onPreSubmit,
  onSubmitSuccess,
  onClose,
  images,
  modelName,
  backgroundImage,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (values) => {
    setLoading(true);

    // TODO - cleanup the code
    document.body.setAttribute(
      "style",
      "width:1470px; height: 820px; position:relative"
    );

    onPreSubmit && onPreSubmit();
    let backgroundBase64 = "";

    if (backgroundImage) {
      const backgroundBlob = await jpegToBlob(backgroundImage);
      backgroundBase64 = await toBase64(backgroundBlob);
    }

    const imagesBase64 = await Promise.all(
      images.map(async (image) => {
        return await prepareImageFromCanva(image.value, backgroundBase64);
      })
    );

    const node = document.getElementById("summaryViewBox");
    const dataUrl = await domtoimage.toPng(node, { quality: 0.95 });
    imagesBase64.push(dataUrl);
    document.body.removeAttribute("style");

    try {
      await api.post("/submitModel", {
        ...values,
        images: imagesBase64,
        modelName: modelName,
        backgroundImage: "",
      });

      setTimeout(() => {
        onSubmitSuccess();
      }, 3000);
      setSubmitted(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error: ", error);
    }
  };
  return (
    <Dialog
      open={modalOpen}
      onClose={onClose}
      disableEscapeKeyDown={loading || submitted}
      disableBackdropClick={loading || submitted}
    >
      {submitted ? (
        <DialogContent>
          <Box textAlign="center" mt={1} mb={1} pl={1} pr={1} paddingY={2}>
            <StyledCheckCircleIcon />
            <Typography variant="h6">
              Your submission has been sent to Knightscope
            </Typography>
            <Box mb={1} />
            <Typography variant="caption">
              You will receive an email once approved
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        <Formik
          initialValues={{
            name: "",
            email: "",
            company: "",
            phone: "",
            comment: "",
          }}
          validationSchema={submitValidationSchema}
          onSubmit={submit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
          }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle disableTypography>
                <Typography variant="h6" component="h2">
                  Submit to Knightscope
                </Typography>
                <Box flex="1" />
                <StyledCircleButton onClick={onClose}>
                  <img src={require("../../assets/close.png")} />
                </StyledCircleButton>
              </DialogTitle>
              <DialogContent>
                <Box display="flex">
                  <Box>
                    <FormControl fullWidth>
                      <StyledFormLabel variant="h6" required>
                        Name
                      </StyledFormLabel>
                      <StyledTextField
                        name="name"
                        placeholder="Your Name"
                        variant="outlined"
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        margin="normal"
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </FormControl>
                    <FormControl fullWidth>
                      <StyledFormLabel variant="h6" required>
                        Email
                      </StyledFormLabel>
                      <StyledTextField
                        placeholder="Your Email"
                        name="email"
                        variant="outlined"
                        type="email"
                        value={values.email}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        margin="normal"
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth>
                      <StyledFormLabel variant="h6">Company</StyledFormLabel>
                      <StyledTextField
                        placeholder="Your Company"
                        name="company"
                        variant="outlined"
                        value={values.company}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        margin="normal"
                        error={Boolean(touched.company && errors.company)}
                        helperText={touched.company && errors.company}
                      />
                    </FormControl>
                    <FormControl fullWidth>
                      <StyledFormLabel variant="h6">
                        Phone Number
                      </StyledFormLabel>
                      <StyledTextField
                        placeholder="Your Phone"
                        name="phone"
                        variant="outlined"
                        value={values.phone}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        margin="normal"
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </FormControl>
                  </Box>
                </Box>
                <FormControl fullWidth>
                  <StyledFormLabel variant="h6">Comment</StyledFormLabel>
                  <StyledTextField
                    placeholder="Add Comment"
                    name="comment"
                    variant="outlined"
                    value={values.comment}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    margin="normal"
                    multiline
                    rows={4}
                    error={Boolean(touched.comment && errors.comment)}
                    helperText={touched.comment && errors.comment}
                  />
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Box flex="1" />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

const prepareImageFromCanva = async (imageUrl, imageBgUrl) => {
  let canvasWidth = 1920;
  let canvasHeight = 1024;
  let backgroundImage = "";

  if (imageBgUrl) {
    backgroundImage = await loadImage(imageBgUrl);
    canvasWidth = backgroundImage.width;
    canvasHeight = backgroundImage.height;
  }

  const canvas = document.createElement("canvas");

  canvas.height = canvasHeight;
  canvas.width = canvasWidth;
  const ctx = canvas.getContext("2d");

  if (backgroundImage) {
    ctx.drawImage(backgroundImage, 0, 0);
  } else {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }

  const modelImage = await loadImage(imageUrl);
  const modelRatio = modelImage.width / modelImage.height;
  const modelHeight = canvasHeight;
  const modelWidth = canvasHeight * modelRatio;
  const widthOffset = (modelWidth - canvasWidth) / 2;
  ctx.drawImage(modelImage, -widthOffset, 0, modelWidth, modelHeight);

  return canvas.toDataURL("image/jpeg", 0.95);
};

const loadImage = (url) => {
  return new Promise((resolve) => {
    const image = new Image();
    image.addEventListener("load", () => {
      resolve(image);
    });

    image.src = url;
  });
};
export default SubmitModal;
